import { Box, FormControlLabel, Switch, Typography } from "@material-ui/core";
import { Alert, Skeleton } from "@material-ui/lab";
import { scaleLinear } from "d3-scale";
import { memo, useMemo, useState } from "react";
import useFetchGraphData from "../../../hooks/useFetchGraphData";
import DepthHeatmapChart from "./DepthHeatmapChart";

// Temperature color scale (same as parameterid 8 in DepthHeatmapChartContainer)
const TEMPERATURE_SCALE = {
  breaks: [0, 8, 13, 16, 19, 21, 22, 23, 24, 26.2],
  colors: [
    "#4e65ac",
    "#6869af",
    "#5ca7ba",
    "#7ecba7",
    "#addeac",
    "#f6fbb5",
    "#ff9b93",
    "#e6665c",
    "#c43a2a",
    "#8b1a0e",
  ],
  rationale:
    "Regulation 38 Aquatic Life 2 standards specify chronic (<26.2°C) and acute (<29.3°C) thresholds. Elevated temperatures can reduce oxygen levels and stress aquatic species.",
};

function TemperatureHeatmapChartContainer({
  start_date,
  end_date,
  height = 400,
  showLegend = true,
  title = null,
  onDateSelect = null,
}) {
  const [showValues, setShowValues] = useState(false);
  const {
    data = [],
    isLoading,
    isError,
  } = useFetchGraphData({ start_date, end_date }, ["thermistor-data"]);

  const colorScale = useMemo(
    () =>
      scaleLinear()
        .domain(TEMPERATURE_SCALE.breaks)
        .range(TEMPERATURE_SCALE.colors),
    []
  );

  const { matrix, dates, unit } = useMemo(() => {
    if (!data.length) return { matrix: [], dates: [], unit: "°C" };

    const depths = [...new Set(data.map((d) => d.depth_value))].sort(
      (a, b) => a - b
    );
    const uniqueDates = [...new Set(data.map((d) => d.rdate))].sort();

    const matrix = depths.map((depth) => ({
      id: String(depth),
      data: uniqueDates.map((date) => {
        const point = data.find(
          (r) => r.depth_value === depth && r.rdate === date
        );
        return { x: date, y: point?.daily_avg ?? null };
      }),
    }));

    return {
      matrix,
      dates: uniqueDates,
      unit: data[0]?.unit_name || "°C",
    };
  }, [data]);

  if (isLoading)
    return (
      <Skeleton variant="rect" height={height} width="100%" animation="wave" />
    );

  if (isError || !matrix.length)
    return (
      <Box
        height={height}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        No data for current selections
      </Box>
    );

  return (
    <>
      {title && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" component="h2">
            {title}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showValues}
                onChange={(e) => setShowValues(e.target.checked)}
                color="primary"
              />
            }
            label="Show values in cells"
          />
        </Box>
      )}
      {!title && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <FormControlLabel
            control={
              <Switch
                checked={showValues}
                onChange={(e) => setShowValues(e.target.checked)}
                color="primary"
              />
            }
            label="Show values in cells"
          />
        </Box>
      )}

      <DepthHeatmapChart
        matrix={matrix}
        dates={dates}
        colorScale={colorScale}
        breaks={showLegend ? TEMPERATURE_SCALE.breaks : []}
        colors={showLegend ? TEMPERATURE_SCALE.colors : []}
        unit={unit}
        height={height}
        showValues={showValues}
        onClick={onDateSelect}
        xAxisFormat="date"
      />

      <Box mt={2} width="100%">
        <Alert severity="info">{TEMPERATURE_SCALE.rationale}</Alert>
      </Box>
      {onDateSelect && (
        <Box mt={1}>
          <Typography variant="body2" color="textSecondary">
            Click on a cell to view 15-minute data for that date.
          </Typography>
        </Box>
      )}
    </>
  );
}

export default memo(TemperatureHeatmapChartContainer);
